import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, AlertTriangle, Download, Loader2, RefreshCw } from "lucide-react";
import {
  getRegistrationReport, getPayrollReport, getAuditReport,
  registrationReportCsv, payrollReportCsv, auditReportCsv, downloadCsv, money,
  type RegistrationReport, type PayrollReport, type AuditReport,
} from "@/lib/financialLedger";

interface Props {
  period: string;
  range?: { start_date?: string; end_date?: string };
}

const FINDING_LABELS: Record<string, string> = {
  registrations_missing_from_ledger: "Registration payments missing from the ledger",
  registration_amount_mismatches: "Registration amounts that do not match the ledger",
  ledger_registration_entries_without_source: "Ledger revenue entries with no registration record",
  payroll_missing_from_ledger: "Payroll payments missing from the ledger",
  payroll_amount_mismatches: "Payroll amounts that do not match the ledger",
  ledger_payroll_entries_without_source: "Ledger payroll entries with no payroll record",
  duplicate_transactions: "Possible duplicate transactions",
  unclear_transactions: "Transactions without a clear description",
  missing_documentation: "Transactions missing documentation",
};

function TieOut({ label, source, ledger, difference }: { label: string; source: number; ledger: number; difference: number }) {
  const ok = difference === 0;
  return (
    <div className={`rounded-lg border p-3 ${ok ? "border-border" : "border-destructive"}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{label}</p>
        {ok ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <AlertTriangle className="h-4 w-4 text-destructive" />}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Source records {money(source)} · Ledger {money(ledger)} ·{" "}
        <span className={ok ? "" : "text-destructive font-medium"}>Difference {money(difference)}</span>
      </p>
    </div>
  );
}

export function LedgerReports({ period, range }: Props) {
  const [tab, setTab] = useState("registrations");
  const [reg, setReg] = useState<RegistrationReport | null>(null);
  const [pay, setPay] = useState<PayrollReport | null>(null);
  const [audit, setAudit] = useState<AuditReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [r, p, a] = await Promise.all([
        getRegistrationReport(period, range),
        getPayrollReport(period, range),
        getAuditReport(period, range),
      ]);
      setReg(r); setPay(p); setAudit(a);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, range?.start_date, range?.end_date]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Audit Reports &amp; Reconciliation Proof</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Every summary figure above is proven here against the original registration and payroll records.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {loading && !audit && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Rebuilding the audit from source records…
          </div>
        )}

        {audit && (
          <Alert variant={(audit as any).reconciled ? "default" : "destructive"}>
            {(audit as any).reconciled ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            <AlertTitle>
              {(audit as any).reconciled ? "This period reconciles exactly" : `${audit.discrepancy_count} unresolved item${audit.discrepancy_count === 1 ? "" : "s"}`}
            </AlertTitle>
            <AlertDescription>{audit.verdict}</AlertDescription>
          </Alert>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="registrations">Registration Payments</TabsTrigger>
            <TabsTrigger value="payroll">Payroll Payments</TabsTrigger>
            <TabsTrigger value="audit">Audit &amp; Proof</TabsTrigger>
          </TabsList>

          {/* -------------------------------------------------- registrations */}
          <TabsContent value="registrations" className="space-y-3">
            {reg && (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="secondary">{reg.totals.source_record_count} payment records</Badge>
                  <Badge variant="secondary">{reg.totals.included_count} counted as revenue</Badge>
                  <Badge variant="outline">{reg.totals.excluded_count} excluded</Badge>
                  {reg.totals.missing_from_ledger > 0 && (
                    <Badge variant="destructive">{reg.totals.missing_from_ledger} missing from ledger</Badge>
                  )}
                  <Button size="sm" variant="outline" className="ml-auto"
                    onClick={() => downloadCsv(`silver-sage-registration-payments-${reg.period.start_date}-to-${reg.period.end_date}.csv`, registrationReportCsv(reg))}>
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                </div>
                <TieOut label="Registration revenue ties out"
                  source={reg.totals.source_total_cents}
                  ledger={reg.totals.ledger_total_cents}
                  difference={reg.totals.difference_cents} />
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Parent</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Counted</TableHead>
                        <TableHead>Ledger</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reg.rows.map((r) => (
                        <TableRow key={r.transaction_id} className={r.included_in_revenue ? "" : "opacity-60"}>
                          <TableCell className="text-xs whitespace-nowrap">{r.date}</TableCell>
                          <TableCell className="text-xs">{r.parent_name ?? "—"}</TableCell>
                          <TableCell className="text-xs">{r.student_name ?? "—"}</TableCell>
                          <TableCell className="text-xs max-w-[200px]"><span className="block truncate">{r.class_title}</span></TableCell>
                          <TableCell className="text-right text-xs">{money(r.amount_cents)}</TableCell>
                          <TableCell className="text-xs">{r.payment_method}</TableCell>
                          <TableCell className="text-xs">
                            {r.included_in_revenue ? "Yes" : <span className="text-muted-foreground">{r.excluded_reason}</span>}
                          </TableCell>
                          <TableCell className="text-xs">
                            <Badge variant={r.ledger_status.startsWith("MISSING") ? "destructive" : "secondary"} className="text-[10px]">
                              {r.ledger_status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </TabsContent>

          {/* -------------------------------------------------------- payroll */}
          <TabsContent value="payroll" className="space-y-3">
            {pay && (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="secondary">{pay.totals.source_record_count} payroll records</Badge>
                  {pay.totals.missing_from_ledger > 0 && (
                    <Badge variant="destructive">{pay.totals.missing_from_ledger} missing from ledger</Badge>
                  )}
                  {pay.totals.mismatched > 0 && <Badge variant="destructive">{pay.totals.mismatched} mismatched</Badge>}
                  <Button size="sm" variant="outline" className="ml-auto"
                    onClick={() => downloadCsv(`silver-sage-payroll-payments-${pay.period.start_date}-to-${pay.period.end_date}.csv`, payrollReportCsv(pay))}>
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                </div>
                <TieOut label="Payroll ties out"
                  source={pay.totals.source_total_cents}
                  ledger={pay.totals.ledger_total_cents}
                  difference={pay.totals.difference_cents} />
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Paid</TableHead>
                        <TableHead>Instructor</TableHead>
                        <TableHead>Pay Period</TableHead>
                        <TableHead>Classes</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead>Ledger</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pay.rows.map((r) => (
                        <TableRow key={r.payroll_id} className={r.voided ? "opacity-60" : ""}>
                          <TableCell className="text-xs whitespace-nowrap">{r.paid_date}</TableCell>
                          <TableCell className="text-xs">{r.instructor_name}</TableCell>
                          <TableCell className="text-xs">{r.pay_period}</TableCell>
                          <TableCell className="text-xs max-w-[180px]"><span className="block truncate">{r.classes.join(", ") || "—"}</span></TableCell>
                          <TableCell className="text-right text-xs">{money(r.amount_cents)}</TableCell>
                          <TableCell className="text-xs">{r.payment_method ?? "—"}</TableCell>
                          <TableCell className="text-xs max-w-[160px]"><span className="block truncate">{r.payment_destination ?? "—"}</span></TableCell>
                          <TableCell className="text-xs">
                            <Badge variant={r.matches ? "secondary" : "destructive"} className="text-[10px]">{r.ledger_status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {pay.by_instructor.length > 0 && (
                  <div className="rounded-lg border border-border p-3">
                    <p className="text-sm font-medium mb-2">Total paid per instructor</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {pay.by_instructor.map((i) => (
                        <li key={i.instructor} className="flex justify-between">
                          <span>{i.instructor} · {i.payments} payment{i.payments === 1 ? "" : "s"}</span>
                          <span className="font-medium text-foreground">{money(i.paid_cents)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ---------------------------------------------------------- audit */}
          <TabsContent value="audit" className="space-y-3">
            {audit && (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    Generated {new Date(audit.generated_at).toLocaleString("en-US")}
                    {audit.reconciled_through ? ` · Reconciled through ${audit.reconciled_through}` : " · Not yet reconciled"}
                  </span>
                  <Button size="sm" variant="outline" className="ml-auto"
                    onClick={() => downloadCsv(`silver-sage-financial-audit-${audit.period.start_date}-to-${audit.period.end_date}.csv`, auditReportCsv(audit))}>
                    <Download className="h-4 w-4 mr-1" /> Export audit report
                  </Button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <TieOut label="Registration revenue"
                    source={audit.proof.registrations.source_total_cents}
                    ledger={audit.proof.registrations.ledger_total_cents}
                    difference={audit.proof.registrations.difference_cents} />
                  <TieOut label="Payroll"
                    source={audit.proof.payroll.source_total_cents}
                    ledger={audit.proof.payroll.ledger_total_cents}
                    difference={audit.proof.payroll.difference_cents} />
                </div>

                <div className="space-y-2">
                  {Object.entries(audit.findings).map(([key, rows]) => (
                    <div key={key} className="rounded-lg border border-border p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">{FINDING_LABELS[key] ?? key.replace(/_/g, " ")}</p>
                        <Badge variant={rows.length ? "destructive" : "secondary"}>{rows.length}</Badge>
                      </div>
                      {rows.length > 0 && (
                        <ul className="mt-2 text-xs text-muted-foreground space-y-0.5 max-h-32 overflow-y-auto">
                          {rows.map((r: any, i: number) => (
                            <li key={i}>{JSON.stringify(r)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
